import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { calcDatePreset } from '../../components/fields/DateRangePicker';
import DateRangeSelect from '../../components/fields/DateRangeSelect';
import { qcResultAPI } from '../../services';

/**
 * #175146 — Tab "Chất lượng người chấm": cho quản lý thấy NGƯỜI CHẤM QC NÀO ẩu.
 * Mỗi dòng 1 người chấm: số set/mục đã chấm · giây/mục trung bình · số lần bị cờ chấm nhanh ·
 * tỉ lệ Không đạt · số mục hậu kiểm + tỉ lệ lệch. Bấm "Xem hậu kiểm" để soi các mục bị lệch.
 */
export default function QcReviewerQualityTab({ branchId, onViewAudits }) {
  const [range, setRange] = useState(() => calcDatePreset('last3months'));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await qcResultAPI.getReviewerQuality({
        branch_id: branchId || undefined,
        from: range.from,
        to: range.to,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.meta || {});
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Không tải được thống kê chất lượng người chấm');
    } finally {
      setLoading(false);
    }
  }, [branchId, range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const suspicious = (r) =>
    r.fast_flags > 0 || (r.mismatch_items > 0) || (r.avg_seconds_per_item !== null && r.avg_seconds_per_item < (meta.min_seconds_per_item || 10));

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <DateRangeSelect
                fromDate={range.from}
                toDate={range.to}
                onChange={(from, to) => setRange({ from, to })}
                defaultPreset="last3months"
              />
            </Col>
            <Col md={5} className="text-muted small">
              Chỉ tính các <strong>phiên đủ set đánh giá</strong> (đã đóng và chấm đủ{' '}
              {meta.min_items_for_evaluation || '—'} mục). Giây/mục là thời gian thao tác thật của phiên
              chia số mục đã chấm.
            </Col>
            <Col md={3} className="text-md-end">
              <Button size="sm" variant="outline-secondary" onClick={fetchData} disabled={loading}>
                {loading ? 'Đang tải…' : 'Tải lại'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-4 text-muted">Không có dữ liệu theo bộ lọc hiện tại.</div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Người chấm QC</th>
                <th>Số set</th>
                <th>Số mục</th>
                <th title="Thời gian thao tác thật của phiên chia số mục đã chấm">Giây/mục</th>
                <th title="Số phiên bị gắn cờ chấm quá nhanh">Cờ chấm nhanh</th>
                <th title="Tỉ lệ mục bị người chấm này chấm Không đạt">Tỉ lệ Không đạt</th>
                <th>Mục hậu kiểm</th>
                <th title="Số mục hậu kiểm bị lệch / tổng mục hậu kiểm">Tỉ lệ lệch</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.qc_employee_id} className={suspicious(r) ? 'table-warning' : ''}>
                  <td>
                    {r.full_name || r.qc_employee_id}
                    {suspicious(r) && <span className="badge bg-warning text-dark ms-2">nghi chấm ẩu</span>}
                  </td>
                  <td>{r.sets}</td>
                  <td>{r.items}</td>
                  <td>{r.avg_seconds_per_item !== null ? `${r.avg_seconds_per_item} s` : '—'}</td>
                  <td className={r.fast_flags > 0 ? 'text-danger fw-semibold' : ''}>{r.fast_flags}</td>
                  <td>{r.fail_rate !== null ? `${r.fail_rate}%` : '—'}</td>
                  <td>{r.audited_items}</td>
                  <td className={r.mismatch_items > 0 ? 'text-danger fw-semibold' : ''}>
                    {r.mismatch_rate !== null ? `${r.mismatch_rate}% (${r.mismatch_items})` : '—'}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => onViewAudits && onViewAudits(r.qc_employee_id)}
                    >
                      Xem hậu kiểm
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
}
